import MeetingRoomWeekSchedular from "../Schedular/MeetingRoomWeekSchedular";
import MeetingRoomMonthSchedular from "../Schedular/MeetingRoomMonthSchedular";

import { Box, Skeleton, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useCallback, useMemo } from "react";
import {
  MeetingRoomTabsHeader,
  MeetingRoomTabsWrapper,
  ReservationButton,
} from "./MeetingRoomTabsStyled";
import { formatToLocalDateTime } from "../../../system/api/util/DateAndTimeUtil";
import {
  DateInfo,
  MeetingRoomEventListItem,
  MeetingRoomStatusInfo,
  OutlookDeepLinkParam,
  RoomColorInfo,
} from "../../../system/types/meetingRoom/MeetingRoom";
import { useMeetingRoom } from "../../../hooks/MeetingRoom/useMeetingRoom";
import dayjs from "dayjs";
import AlertModal from "../../Common/AlertModal";

type MeetingRoomTabsProps = {
  getMeetingRoomEvents: () => void;
  meetingRoomIsLoading: boolean;
  selectedTab: "week" | "month";
  handleSelectedTab: (tab: "week" | "month") => void;
  selectedMeetingRoom: MeetingRoomStatusInfo | null;
  dateInfo: DateInfo;
  handleChangeDateInfo: (valueObject: Partial<DateInfo>) => void;
  colorList: RoomColorInfo[];
  meetingRoomEventList: MeetingRoomEventListItem[];
  handleOpenOutlookPopup: (params: OutlookDeepLinkParam[]) => void;
  handleOpenGuidePopup: (
    status: boolean,
    params: OutlookDeepLinkParam[]
  ) => void;
};
const MeetingRoomTabs = ({
  getMeetingRoomEvents,
  meetingRoomIsLoading,
  selectedTab,
  handleSelectedTab,
  selectedMeetingRoom,
  dateInfo,
  handleChangeDateInfo,
  colorList,
  meetingRoomEventList,
  handleOpenOutlookPopup,
  handleOpenGuidePopup,
}: MeetingRoomTabsProps) => {
  const { checkShowPopup, getIsAvailable } = useMeetingRoom();

  // OutlookDeepLink 팝업.
  // 선택된 회의실이 있으면, cc에 추가하여 Open
  const handleOpenGuideOrDeepLinkPopup = useCallback(
    (params = []) => {
      const showPopup = checkShowPopup();
      const copiedParams = params ? params.slice() : [];

      if (selectedMeetingRoom !== null)
        copiedParams.push({
          key: "cc",
          value: `${selectedMeetingRoom.roomInfo.displayName} <${selectedMeetingRoom.roomInfo.mail}>`,
        });
      if (showPopup) {
        handleOpenGuidePopup(true, copiedParams);
      } else {
        handleOpenOutlookPopup(params);
      }
    },
    [
      selectedMeetingRoom,
      handleOpenGuidePopup,
      handleOpenOutlookPopup,
      checkShowPopup,
    ]
  );

  // 현재 Popup Open 여부를 체크 후 Popup
  const handleOpenCurTimeDeepLinkPopup = useCallback(
    (params = []) => {
      const isAvailable = getIsAvailable(
        meetingRoomEventList,
        dayjs().format("YYYY-MM-DDTHH:mm")
      );
      if (isAvailable) {
        handleOpenGuideOrDeepLinkPopup(params);
      } else {
        AlertModal("check", "현재 시간에 이미 등록된 예약이 있습니다.");
      }
    },
    [getIsAvailable, handleOpenGuideOrDeepLinkPopup, meetingRoomEventList]
  );

  // 일정이 여러날이면, 중간 날짜에 대한 시간을 하루종일 시간으로 변경
  const splitMultiDayEvent = useCallback((event: any): any[] => {
    const result: any[] = [];

    // 이벤트 정보의 시작과 종료 시간
    const start = dayjs(formatToLocalDateTime(event.start));
    const end = dayjs(formatToLocalDateTime(event.end));
    // 시작일과 종료일 사이의 일(day) 차이를 계산
    const diff = end.startOf("day").diff(start.startOf("day"), "day");

    for (let i = 0; i <= diff; i++) {
      const currentDay = start.add(i, "day");

      // 시작 시간 설정:
      // - 첫 날이면 원래 이벤트 시작 시간 사용
      // - 그 외의 날은 00:00:01로 설정
      const segmentStart =
        i === 0 ? start : currentDay.hour(0).minute(0).second(1);

      // 종료 시간 설정:
      // - 마지막 날이면 원래 이벤트 종료 시간 사용
      // - 그 외의 날은 24:00:00 (== 다음 날 00:00:00)로 설정
      const segmentEnd =
        i === diff ? end : currentDay.hour(24).minute(0).second(0);

      // 날짜 별로 분할된 이벤트 정보를 배열에 추가
      result.push({
        ...event,
        Id: `${event.id}-${i}`, // 유니크하게
        Subject: event.subject.split("(")[0], // 제목에서 괄호 앞부분만 사용
        StartTime: segmentStart.toDate(),
        EndTime: segmentEnd.toDate(),
      });
    }

    return result;
  }, []);

  // Schedule에 들어갈 EventList 가공
  const meetingRoomScheduleList = useMemo(() => {
    if (!meetingRoomEventList) return [];

    const result = meetingRoomEventList.flatMap((el) => {
      const start = dayjs(formatToLocalDateTime(el.start));
      const end = dayjs(formatToLocalDateTime(el.end));
      const isMultiDay = !start.isSame(end, "day");

      // 시작일과 종료일이 다른 날짜. 즉, 여러 날에 걸쳐 있는 경우
      if (isMultiDay) {
        return splitMultiDayEvent(el); // 하루 단위로 쪼갬
      }

      return [
        {
          ...el,
          Id: el.id,
          Subject: el.subject.split("(")[0],
          StartTime: start.toDate(),
          EndTime: end.toDate(),
          IsAllDay: el.isAllDay ?? false,
        },
      ];
    });

    return result;
  }, [meetingRoomEventList]);

  return (
    <MeetingRoomTabsWrapper>
      <MeetingRoomTabsHeader>
        <ToggleButtonGroup
          value={selectedTab}
          exclusive
          onChange={(e, v) => handleSelectedTab(v)}
          sx={{ height: "32px" }}
        >
          <ToggleButton value="week">주</ToggleButton>
          <ToggleButton value="month">월</ToggleButton>
        </ToggleButtonGroup>

        <ReservationButton onClick={() => handleOpenCurTimeDeepLinkPopup([])}>
          예약 하기
        </ReservationButton>
      </MeetingRoomTabsHeader>

      {meetingRoomIsLoading ? (
        <Box>
          <Skeleton variant="rectangular" height={600} />
        </Box>
      ) : selectedTab === "week" ? (
        <MeetingRoomWeekSchedular
          getMeetingRoomEvents={getMeetingRoomEvents}
          dateInfo={dateInfo}
          selectedMeetingRoom={selectedMeetingRoom}
          handleChangeDateInfo={handleChangeDateInfo}
          colorList={colorList}
          meetingRoomScheduleList={meetingRoomScheduleList}
          handleClickOpenOutlookPopup={handleOpenGuideOrDeepLinkPopup}
        />
      ) : (
        <MeetingRoomMonthSchedular
          getMeetingRoomEvents={getMeetingRoomEvents}
          selectedMeetingRoom={selectedMeetingRoom}
          dateInfo={dateInfo}
          handleChangeDateInfo={handleChangeDateInfo}
          colorList={colorList}
          meetingRoomScheduleList={meetingRoomScheduleList}
          handleClickOpenOutlookPopup={handleOpenGuideOrDeepLinkPopup}
        />
      )}
    </MeetingRoomTabsWrapper>
  );
};

export default MeetingRoomTabs;
